import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Paper,
    Box,
    Typography,
    Avatar,
    Chip,
    Alert,
    CircularProgress,
    Button,
    Card,
    CardContent,
    Grid,
    Divider,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    QrCode as QrCodeIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    AccountCircle as AccountCircleIcon,
    Warning as WarningIcon,
    CameraAlt as CameraAltIcon,
    Terminal as TerminalIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { memberProfileAPI, amenityAPI, classAPI, checkinAPI } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

interface MemberData {
    id: string;
    userId: string;
    name: string;
    email: string;
    membershipNumber: string;
    membershipTier: string;
    status: string;
    isActive: boolean;
    profilePictureUrl?: string;
    phone?: string;
    dateOfBirth?: string;
    joinDate?: string;
}

interface DecryptedQRData {
    success: boolean;
    member: MemberData;
    qrCodeTimestamp: string;
}

interface RecentCheckin {
    id: number;
    checkinTime: string;
    checkoutTime: string | null;
    facilityArea: string | null;
    notes: string | null;
    member: {
        id: string;
        firstName: string;
        lastName: string;
        membershipNumber: string;
        membershipTier: string;
        profilePictureUrl?: string;
    } | null;
}

interface SearchResult {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    membershipNumber: string;
    membershipTier: {
        id: string;
        name: string;
    };
    status: string;
    profilePictureUrl?: string;
    phone?: string;
}

type ScanState = 'ready' | 'scanning' | 'success' | 'error' | 'not-found';

const KioskView: React.FC = () => {
    const navigate = useNavigate();
    const [qrCodeInput, setQrCodeInput] = useState('');
    const [memberData, setMemberData] = useState<MemberData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [qrCodeTimestamp, setQrCodeTimestamp] = useState<string | null>(null);
    const [scanState, setScanState] = useState<ScanState>('ready');
    const [todaysBookings, setTodaysBookings] = useState<any[]>([]);
    const [countdown, setCountdown] = useState<number>(5);
    const [debugLogs, setDebugLogs] = useState<string[]>([]);
    const [showDebugConsole, setShowDebugConsole] = useState(false);
    const [recentCheckins, setRecentCheckins] = useState<RecentCheckin[]>([]);
    
    // Manual check-in state
    const [showManualCheckinDialog, setShowManualCheckinDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    
    const inputRef = useRef<HTMLInputElement>(null);
    const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Debug logging function
    const addDebugLog = (message: string) => {
        const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
        const logMessage = `[${timestamp}] ${message}`;
        console.log(logMessage);
        setDebugLogs(prev => [...prev, logMessage]);
    };

    // Fetch recent check-ins
    const fetchRecentCheckins = async () => {
        try {
            addDebugLog('Fetching recent check-ins');
            const response = await checkinAPI.getRecent();
            setRecentCheckins(response.data);
            addDebugLog(`Fetched ${response.data.length} recent check-ins`);
        } catch (error) {
            console.error('Error fetching recent check-ins:', error);
            addDebugLog('Failed to fetch recent check-ins');
        }
    };

    // Auto-focus input field on mount and after each scan
    useEffect(() => {
        addDebugLog('Component mounted or memberData changed');
        const focusInput = () => {
            if (inputRef.current && !memberData && !showManualCheckinDialog) {
                inputRef.current.focus();
                addDebugLog('Input field focused');
            }
        };
        
        focusInput();
        
        // Fetch recent check-ins on mount
        if (!memberData) {
            fetchRecentCheckins();
        }
        
        // Set up interval to keep refocusing if it loses focus
        const focusInterval = setInterval(() => {
            if (inputRef.current && !memberData && !showManualCheckinDialog && document.activeElement !== inputRef.current) {
                addDebugLog('Input lost focus, refocusing...');
                inputRef.current.focus();
            }
        }, 500);
        
        return () => clearInterval(focusInterval);
    }, [memberData, showManualCheckinDialog]);

    // Countdown timer effect - COMMENTED OUT FOR FASTER CHECK-INS
    // useEffect(() => {
    //     if (memberData && countdown > 0) {
    //         countdownTimerRef.current = setTimeout(() => {
    //             setCountdown(countdown - 1);
    //         }, 1000);
    //     } else if (memberData && countdown === 0) {
    //         resetView();
    //     }

    //     return () => {
    //         if (countdownTimerRef.current) {
    //             clearTimeout(countdownTimerRef.current);
    //         }
    //     };
    // }, [memberData, countdown]);

    // Fetch today's bookings for member
    const fetchTodaysBookings = async (memberId: string) => {
        try {
            const today = new Date().toISOString().split('T')[0];
            
            // Fetch amenity bookings
            const amenityBookingsResponse = await amenityAPI.getAllAmenityBookings({
                memberProfileId: memberId,
                date: today
            });

            // Fetch class bookings  
            const classBookingsResponse = await classAPI.getUserBookings();
            const todaysClasses = (classBookingsResponse.data || []).filter((b: any) => {
                const bookingDate = new Date(b.classInstance?.sessionDate).toISOString().split('T')[0];
                return bookingDate === today;
            });
            
            const allBookings = [
                ...(amenityBookingsResponse.data?.bookings || []).map((b: any) => ({
                    type: 'Amenity',
                    name: b.amenity?.name || 'Amenity',
                    time: b.startTime,
                    date: b.date,
                })),
                ...todaysClasses.map((b: any) => ({
                    type: 'Class',
                    name: b.classInstance?.class?.name || 'Class',
                    time: b.classInstance?.startTime,
                    date: b.classInstance?.sessionDate,
                }))
            ];

            setTodaysBookings(allBookings);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setTodaysBookings([]);
        }
    };

    // Handle QR code scanner input
    const handleQrCodeInput = async (value: string) => {
        addDebugLog(`handleQrCodeInput called with value length: ${value.length}`);
        if (!value.trim()) {
            addDebugLog('Value is empty after trim, returning');
            return;
        }
        
        addDebugLog(`Processing QR code: ${value.substring(0, 50)}...`);
        setScanState('scanning');
        
        // Process the scanned QR code
        await decryptAndFetchMember(value.trim());
        
        // Clear input for next scan
        setQrCodeInput('');
        addDebugLog('Input cleared for next scan');
    };

    // Handle input change (from QR scanner or keyboard)
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        addDebugLog(`Input change detected, value length: ${value.length}`);
        setQrCodeInput(value);
        
        // Show "Processing" state as soon as we start receiving data
        if (value.length > 0 && scanState === 'ready') {
            setScanState('scanning');
            addDebugLog('Scan state changed to scanning (receiving data)');
        }
        
        // QR scanners typically add a newline/enter at the end
        // Process immediately if we detect a complete scan
        if (value.includes('\n') || value.includes('\r')) {
            addDebugLog('Newline detected, processing immediately');
            const cleanValue = value.replace(/[\n\r]/g, '').trim();
            handleQrCodeInput(cleanValue);
        } else if (value.length > 20) {
            // Some scanners might not send newline, so process if we have substantial data
            addDebugLog(`Substantial data detected (${value.length} chars), checking for processing`);
            // Wait a brief moment to see if more data comes (reduced to 50ms for faster processing)
            setTimeout(() => {
                if (qrCodeInput === value) {
                    addDebugLog('No new data after timeout, processing now');
                    handleQrCodeInput(value);
                }
            }, 50);
        }
    };

    // Handle manual form submission (if user pastes and presses enter)
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        addDebugLog('Form submitted');
        handleQrCodeInput(qrCodeInput);
    };

    // Handle key press for enter key
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        addDebugLog(`Key pressed: ${e.key}`);
        if (e.key === 'Enter') {
            e.preventDefault();
            addDebugLog('Enter key detected, processing input');
            handleQrCodeInput(qrCodeInput);
        }
    };

    const decryptAndFetchMember = async (encryptedData: string) => {
        addDebugLog('Starting decryptAndFetchMember');
        setLoading(true);
        setError(null);

        try {
            addDebugLog(`Sending decrypt request with data: ${encryptedData.substring(0, 50)}...`);
            const response = await memberProfileAPI.decryptQRCode(encryptedData);
            addDebugLog(`Received response: ${JSON.stringify(response.data)}`);
            const data: DecryptedQRData = response.data;

            if (data.success && data.member) {
                addDebugLog(`Member found: ${data.member.name} (${data.member.membershipNumber})`);
                setScanState('success');
                // COMMENTED OUT FOR FASTER CHECK-INS - No longer showing member data
                // setMemberData(data.member);
                // setQrCodeTimestamp(data.qrCodeTimestamp);
                // setCountdown(5); // Reset countdown
                
                // Create check-in record
                try {
                    addDebugLog('Creating check-in record');
                    await checkinAPI.create({
                        userId: data.member.userId,
                        facilityArea: 'Kiosk Entry',
                        notes: 'QR Code scan check-in'
                    });
                    addDebugLog('Check-in record created successfully');
                    // Refresh recent check-ins list in background (non-blocking for faster UI response)
                    fetchRecentCheckins();
                } catch (checkinErr: any) {
                    console.error('Error creating check-in record:', checkinErr);
                    addDebugLog(`Check-in creation failed: ${checkinErr.message}`);
                    // Don't block the user flow if check-in fails
                }
                
                // COMMENTED OUT FOR FASTER CHECK-INS - No longer fetching bookings
                // Fetch today's bookings
                // addDebugLog('Fetching today\'s bookings');
                // await fetchTodaysBookings(data.member.id);
                
                // Reset to ready state quickly for next scan
                setTimeout(() => {
                    setScanState('ready');
                    addDebugLog('Reset to ready state for next scan');
                }, 500); // Reduced from 1000ms for faster check-ins
            } else {
                addDebugLog('Member not found - response success was false');
                setScanState('not-found');
                setError('Member not found in database');
            }
        } catch (err: any) {
            addDebugLog(`Error occurred: ${err.message}`);
            console.error('Error decrypting QR code:', err);
            
            // Check if it's a 404 (member not found) or decryption error
            if (err.response?.status === 404 || err.response?.data?.message?.includes('not found')) {
                addDebugLog('404 error - member not found');
                setScanState('not-found');
                setError('Member not found in database');
            } else {
                addDebugLog(`Decryption error: ${err.response?.data?.message || err.message}`);
                setScanState('error');
                const errorMessage = err.response?.data?.message || 'Failed to decrypt QR code. Please ensure it is a valid membership QR code.';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
            addDebugLog('decryptAndFetchMember completed');
        }
    };

    const resetView = () => {
        addDebugLog('Resetting view');
        setMemberData(null);
        setError(null);
        setQrCodeTimestamp(null);
        setQrCodeInput('');
        setScanState('ready');
        setTodaysBookings([]);
        setCountdown(5);
        
        // Clear countdown timer
        if (countdownTimerRef.current) {
            clearTimeout(countdownTimerRef.current);
        }
        
        // Refocus input field
        if (inputRef.current) {
            inputRef.current.focus();
            addDebugLog('Input refocused after reset');
        }
    };

    const getMembershipStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
                return 'success';
            case 'inactive':
            case 'suspended':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Manual check-in functions
    const handleSearchMembers = async () => {
        if (!searchQuery.trim()) {
            setSearchError('Please enter a search term');
            return;
        }

        setSearchLoading(true);
        setSearchError(null);

        try {
            const response = await memberProfileAPI.getAllMemberProfiles({ search: searchQuery });
            setSearchResults(response.data);
            
            if (response.data.length === 0) {
                setSearchError('No members found matching your search');
            }
        } catch (error: any) {
            console.error('Error searching members:', error);
            setSearchError(error.response?.data?.message || 'Failed to search members');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleManualCheckin = async (member: SearchResult) => {
        try {
            addDebugLog(`Manual check-in for member: ${member.firstName} ${member.lastName}`);
            
            // Create the check-in record
            await checkinAPI.create({
                userId: member.userId,
                facilityArea: 'Kiosk Entry - Manual',
                notes: 'Manual check-in via kiosk'
            });
            
            addDebugLog('Manual check-in record created successfully');
            
            // COMMENTED OUT FOR FASTER CHECK-INS - No longer showing member data
            // Set member data to show success screen
            // const memberDataForDisplay: MemberData = {
            //     id: member.id,
            //     userId: member.userId,
            //     name: `${member.firstName} ${member.lastName}`,
            //     email: member.email,
            //     membershipNumber: member.membershipNumber,
            //     membershipTier: member.membershipTier.name,
            //     status: member.status,
            //     isActive: member.status.toLowerCase() === 'active',
            //     profilePictureUrl: member.profilePictureUrl,
            //     phone: member.phone,
            // };
            
            setScanState('success');
            // setMemberData(memberDataForDisplay);
            // setQrCodeTimestamp(new Date().toISOString());
            // setCountdown(5);
            
            // COMMENTED OUT FOR FASTER CHECK-INS - No longer fetching bookings
            // Fetch today's bookings
            // await fetchTodaysBookings(member.id);
            
            // Refresh recent check-ins in background (non-blocking)
            fetchRecentCheckins();
            
            // Reset to ready state quickly for next scan
            setTimeout(() => {
                setScanState('ready');
                addDebugLog('Reset to ready state after manual check-in');
            }, 500); // Reduced from 1000ms for faster check-ins
            
            // Close the dialog
            setShowManualCheckinDialog(false);
            setSearchQuery('');
            setSearchResults([]);
            setSearchError(null);
        } catch (error: any) {
            console.error('Error during manual check-in:', error);
            setSearchError(error.response?.data?.message || 'Failed to check in member');
            addDebugLog(`Manual check-in failed: ${error.message}`);
        }
    };

    const handleOpenManualCheckin = () => {
        setShowManualCheckinDialog(true);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError(null);
    };

    const handleCloseManualCheckin = () => {
        setShowManualCheckinDialog(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError(null);
    };

    return (
        <>
            {/* CSS Animation for pulse effect */}
            <style>
                {`
                    @keyframes pulse {
                        0% {
                            opacity: 1;
                            transform: scale(1);
                        }
                        50% {
                            opacity: 0.7;
                            transform: scale(1.05);
                        }
                        100% {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                `}
            </style>
            
            <Container 
                maxWidth="lg" 
                sx={{ mt: 4, mb: 4 }}
                onClick={() => {
                    // Ensure input stays focused when clicking anywhere on the page
                    if (inputRef.current && !memberData && !showManualCheckinDialog) {
                        inputRef.current.focus();
                        addDebugLog('Container clicked - refocusing input');
                    }
            }}
        >
            <Paper elevation={3} sx={{ p: 4 }}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center">
                        <QrCodeIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                        <Typography variant="h4" component="h1">
                            Member Verification Kiosk
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddIcon />}
                        onClick={handleOpenManualCheckin}
                        size="large"
                    >
                        Manual Check-In
                    </Button>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Main Content */}
                {!memberData && (
                    <Box>
                        {/* Visual Feedback Icons */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center',
                                mb: 3,
                                position: 'relative'
                            }}
                        >
                            {/* Scan State Icon */}
                            {scanState === 'ready' && (
                                <>
                                    {/* Show processing state when data is being received */}
                                    {qrCodeInput.length > 0 ? (
                                        <>
                                            <CircularProgress size={120} sx={{ mb: 2 }} />
                                            <Typography variant="h6" gutterBottom>
                                                Processing...
                                            </Typography>
                                        </>
                                    ) : document.activeElement !== inputRef.current ? (
                                        /* Show red camera and click prompt when focus is lost */
                                        <>
                                            <CameraAltIcon 
                                                sx={{ 
                                                    fontSize: 120, 
                                                    color: 'error.main', 
                                                    mb: 2,
                                                    cursor: 'pointer',
                                                    animation: 'pulse 2s infinite'
                                                }} 
                                            />
                                            <Typography 
                                                variant="h6" 
                                                gutterBottom 
                                                sx={{ color: 'error.main', cursor: 'pointer' }}
                                            >
                                                Click here to activate scanner
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                The scanner input field needs focus to work
                                            </Typography>
                                        </>
                                    ) : (
                                        /* Normal ready state */
                                        <>
                                            <CameraAltIcon sx={{ fontSize: 120, color: 'primary.main', mb: 2 }} />
                                            <Typography variant="h6" gutterBottom>
                                                Ready to Scan
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Scan a member's QR code using the barcode scanner
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}

                            {scanState === 'scanning' && (
                                <>
                                    <CircularProgress size={120} sx={{ mb: 2 }} />
                                    <Typography variant="h6" gutterBottom>
                                        Processing...
                                    </Typography>
                                </>
                            )}

                            {scanState === 'success' && !memberData && (
                                <>
                                    <CheckCircleIcon sx={{ fontSize: 120, color: 'success.main', mb: 2 }} />
                                    <Typography variant="h6" color="success.main">
                                        Scan Successful
                                    </Typography>
                                </>
                            )}

                            {scanState === 'error' && (
                                <>
                                    <ErrorIcon sx={{ fontSize: 120, color: 'error.main', mb: 2 }} />
                                    <Typography variant="h6" color="error.main" gutterBottom>
                                        Scan Failed
                                    </Typography>
                                    <Button 
                                        variant="contained" 
                                        onClick={resetView}
                                        sx={{ mt: 2 }}
                                    >
                                        Try Again
                                    </Button>
                                </>
                            )}

                            {scanState === 'not-found' && (
                                <>
                                    <WarningIcon sx={{ fontSize: 120, color: 'warning.main', mb: 2 }} />
                                    <Typography variant="h6" color="warning.main" gutterBottom>
                                        Member Not Found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        This member could not be found in the database
                                    </Typography>
                                    <Button 
                                        variant="contained" 
                                        onClick={resetView}
                                        sx={{ mt: 2 }}
                                    >
                                        Try Again
                                    </Button>
                                </>
                            )}

                            {/* Hidden Input Field for QR Scanner */}
                            <Box 
                                component="form" 
                                onSubmit={handleSubmit}
                                sx={{ 
                                    position: 'absolute',
                                    left: -9999,
                                    width: 1,
                                    height: 1,
                                    overflow: 'hidden',
                                }}
                            >
                                <TextField
                                    inputRef={inputRef}
                                    value={qrCodeInput}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    onFocus={() => addDebugLog('Input field gained focus')}
                                    onBlur={(e) => {
                                        addDebugLog('Input field lost focus - refocusing');
                                        // Immediately refocus if we're still in ready state and dialog is not open
                                        setTimeout(() => {
                                            if (inputRef.current && !memberData && !showManualCheckinDialog) {
                                                inputRef.current.focus();
                                            }
                                        }, 10);
                                    }}
                                    placeholder="Ready to scan..."
                                    fullWidth
                                    autoFocus
                                    tabIndex={-1}
                                />
                            </Box>
                        </Box>

                        {/* Error Message */}
                        {error && scanState === 'error' && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Instructions */}
                        {/* {scanState === 'ready' && !loading && (
                            <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Instructions
                                    </Typography>
                                    <Typography variant="body2" component="div">
                                        <ol>
                                            <li>Scan the member's QR code with your barcode scanner</li>
                                            <li>The system will automatically verify the member</li>
                                            <li>Member information will be displayed for verification</li>
                                        </ol>
                                    </Typography>
                                </CardContent>
                            </Card>
                        )} */}

                        {/* Recent Check-ins */}
                        {recentCheckins.length > 0 && (
                            <Card sx={{ mt: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Recent Check-ins
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 500 }}>
                                        <Table>
                                            <TableHead>
                                            <TableRow>
                                                <TableCell>Member</TableCell>
                                                <TableCell>Membership #</TableCell>
                                                <TableCell>Tier</TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Time</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recentCheckins.map((checkin) => (
                                                <TableRow 
                                                    key={checkin.id}
                                                    component="tr"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (checkin.member?.id) {
                                                            console.log('Navigating to:', `/member-profiles/${checkin.member.id}`);
                                                            navigate(`/member-profiles/${checkin.member.id}`);
                                                        }
                                                    }}
                                                    sx={{
                                                        cursor: checkin.member ? 'pointer' : 'default',
                                                        '&:hover': checkin.member ? {
                                                            backgroundColor: 'action.hover',
                                                        } : {},
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center">
                                                            {checkin.member?.profilePictureUrl ? (
                                                                <Avatar
                                                                    src={checkin.member.profilePictureUrl.startsWith('http') ? checkin.member.profilePictureUrl : `${import.meta.env.VITE_API_URL}${checkin.member.profilePictureUrl}`}
                                                                    sx={{ width: 32, height: 32, mr: 1 }}
                                                                />
                                                            ) : (
                                                                <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                                                    <AccountCircleIcon />
                                                                </Avatar>
                                                            )}
                                                            <Typography variant="body2">
                                                                {checkin.member ? `${checkin.member.firstName} ${checkin.member.lastName}` : 'Unknown'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {checkin.member?.membershipNumber || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {checkin.member?.membershipTier || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(checkin.checkinTime)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(checkin.checkinTime).toLocaleTimeString('en-US', {
                                                            hour: 'numeric',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        })}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                )}

                {/* Member Data Display - COMMENTED OUT FOR FASTER CHECK-INS */}
                {/* {memberData && (
                    <Box>
                        {/* Warning for Inactive/Pending Members - Moved to top */}
                        {/* {!memberData.isActive && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                <Typography variant="body1" fontWeight="bold">
                                    This member's account is {memberData.status}
                                </Typography>
                                <Typography variant="body2">
                                    Please verify with management before granting access.
                                </Typography>
                            </Alert>
                        )} */}

                        {/* <Card elevation={2} sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={3}>
                                    {/* Profile Picture and Status */}
                                    {/* <Grid size={{ xs: 12, md: 4 }}>
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            {memberData.profilePictureUrl ? (
                                                <Avatar
                                                    src={memberData.profilePictureUrl.startsWith('http') ? memberData.profilePictureUrl : `${import.meta.env.VITE_API_URL}${memberData.profilePictureUrl}`}
                                                    sx={{ width: 150, height: 150, mb: 2 }}
                                                />
                                            ) : (
                                                <Avatar sx={{ width: 150, height: 150, mb: 2 }}>
                                                    <AccountCircleIcon sx={{ fontSize: 100 }} />
                                                </Avatar>
                                            )}
                                            
                                            <Chip
                                                icon={memberData.isActive ? <CheckCircleIcon /> : <ErrorIcon />}
                                                label={memberData.status.toUpperCase()}
                                                color={getMembershipStatusColor(memberData.status)}
                                                sx={{ mb: 1 }}
                                            />
                                            
                                            <Typography variant="caption" color="text.secondary">
                                                QR Code Generated: {qrCodeTimestamp ? formatDate(qrCodeTimestamp) : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Grid> */}

                                    {/* Member Information */}
                                    {/* <Grid size={{ xs: 12, md: 8 }}>
                                        <Typography variant="h5" gutterBottom>
                                            {memberData.name}
                                        </Typography>
                                        
                                        <Divider sx={{ my: 2 }} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Member Number
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {memberData.membershipNumber}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Membership Tier
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {memberData.membershipTier}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Email
                                                </Typography>
                                                <Typography variant="body1">
                                                    {memberData.email}
                                                </Typography>
                                            </Grid>
                                            
                                            {memberData.phone && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Phone
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {memberData.phone}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            
                                            {memberData.dateOfBirth && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Date of Birth
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatDate(memberData.dateOfBirth)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            
                                            {memberData.joinDate && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Member Since
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatDate(memberData.joinDate)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid> */}
                                {/* </Grid>
                            </CardContent>
                        </Card> */}

                        {/* Today's Bookings */}
                        {/* {todaysBookings.length > 0 && (
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Today's Bookings
                                    </Typography>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Time</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {todaysBookings.map((booking, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{booking.type}</TableCell>
                                                    <TableCell>{booking.name}</TableCell>
                                                    <TableCell>{booking.time}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )} */}

                        {/* Countdown Timer */}
                        {/* <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Returning to scan mode in {countdown} seconds...
                            </Typography>
                            <CircularProgress 
                                variant="determinate" 
                                value={(5 - countdown) * 20} 
                                size={60}
                                sx={{ mt: 2 }}
                            />
                        </Box> */}
                    {/* </Box>
                )} */}
            </Paper>

            {/* Debug Console Toggle Button */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 1000,
                }}
            >
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setShowDebugConsole(!showDebugConsole)}
                    sx={{
                        minWidth: 60,
                        minHeight: 60,
                        borderRadius: '50%',
                        boxShadow: 3,
                    }}
                >
                    <TerminalIcon sx={{ fontSize: 30 }} />
                </Button>
            </Box>

            {/* Debug Console */}
            {showDebugConsole && (
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: 100,
                        right: 20,
                        width: 600,
                        maxHeight: 400,
                        zIndex: 1000,
                        p: 2,
                        backgroundColor: '#1e1e1e',
                        color: '#00ff00',
                        fontFamily: 'monospace',
                        overflow: 'auto',
                        boxShadow: 6,
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ color: '#00ff00' }}>
                            Debug Console
                        </Typography>
                        <Box>
                            <Button
                                size="small"
                                onClick={() => setDebugLogs([])}
                                sx={{ color: '#00ff00', mr: 1 }}
                            >
                                Clear
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setShowDebugConsole(false)}
                                sx={{ color: '#ff0000' }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                    <Divider sx={{ borderColor: '#00ff00', mb: 2 }} />
                    <Box sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                        {debugLogs.length === 0 ? (
                            <Typography sx={{ color: '#888' }}>
                                No debug logs yet. Scan a QR code to see activity.
                            </Typography>
                        ) : (
                            debugLogs.map((log, index) => (
                                <Box key={index} sx={{ mb: 0.5, wordBreak: 'break-all' }}>
                                    {log}
                                </Box>
                            ))
                        )}
                    </Box>
                    <Divider sx={{ borderColor: '#00ff00', my: 2 }} />
                    <Box sx={{ fontSize: '0.85rem' }}>
                        <Typography sx={{ color: '#ffff00', mb: 1 }}>
                            Current State:
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                            <div>Scan State: {scanState}</div>
                            <div>Input Value: "{qrCodeInput}"</div>
                            <div>Input Length: {qrCodeInput.length}</div>
                            <div>Loading: {loading ? 'Yes' : 'No'}</div>
                            <div>Member Data: {memberData ? 'Present' : 'None'}</div>
                            <div>Input Focused: {document.activeElement === inputRef.current ? 'Yes' : 'No'}</div>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Manual Check-In Dialog */}
            <Dialog
                open={showManualCheckinDialog}
                onClose={handleCloseManualCheckin}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Manual Check-In</Typography>
                        <IconButton onClick={handleCloseManualCheckin} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Search Members"
                            placeholder="Enter name, email, or membership number"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchMembers();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleSearchMembers}
                                            disabled={searchLoading}
                                            edge="end"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {searchLoading && (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress />
                            </Box>
                        )}

                        {searchError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {searchError}
                            </Alert>
                        )}

                        {searchResults.length > 0 && !searchLoading && (
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                    Search Results ({searchResults.length})
                                </Typography>
                                <TableContainer sx={{ maxHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto' }}>
                                    <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Member</TableCell>
                                            <TableCell>Membership #</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Tier</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {searchResults.map((member) => (
                                            <TableRow key={member.id}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center">
                                                        {member.profilePictureUrl ? (
                                                            <Avatar
                                                                src={member.profilePictureUrl.startsWith('http') ? member.profilePictureUrl : `${import.meta.env.VITE_API_URL}${member.profilePictureUrl}`}
                                                                sx={{ width: 32, height: 32, mr: 1 }}
                                                            />
                                                        ) : (
                                                            <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                                                <AccountCircleIcon />
                                                            </Avatar>
                                                        )}
                                                        <Typography variant="body2">
                                                            {member.firstName} {member.lastName}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{member.membershipNumber}</TableCell>
                                                <TableCell>{member.email}</TableCell>
                                                <TableCell>{member.membershipTier.name}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={member.status}
                                                        color={getMembershipStatusColor(member.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handleManualCheckin(member)}
                                                        startIcon={<CheckCircleIcon />}
                                                    >
                                                        Check In
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {searchResults.length === 0 && searchQuery && !searchLoading && !searchError && (
                            <Box display="flex" justifyContent="center" py={3}>
                                <Typography variant="body2" color="text.secondary">
                                    Enter a search term and click the search icon
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseManualCheckin}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
        </>
    );
};

export default KioskView;
